import { ArrowDown } from 'lucide-react';

/**
 * Slim proportional funnel (design prototype `funnelSlim`): each stage is a
 * row of label / thin bar / value, and between stages sits a loss row —
 * "Lost before trial: −230,589 (92.8%)". Bar width is proportional to real
 * volume, which is the whole story these funnels tell; successive stages
 * fade slightly like the prototype.
 *
 * Replaces the stretched-SVG trapezoids in ConversionFunnel — those hid the
 * scale of the drop (sqrt scaling) and could not express the loss labels.
 */
export interface FunnelStage {
  label: string;
  value: number;
  /** e.g. "Lost before trial" — shown on the loss row ABOVE this stage. */
  lossLabel?: string;
}

export default function FunnelBars({
  stages,
  color,
}: {
  stages: FunnelStage[];
  color: string;
}) {
  const shown = stages.filter((s) => s.value != null && !Number.isNaN(s.value));
  if (shown.length === 0) return null;
  const max = shown[0].value || 1;

  return (
    <div className="flex flex-col gap-0.5">
      {shown.map((s, i) => {
        const prev = shown[i - 1];
        const lost = prev ? prev.value - s.value : 0;
        return (
          <div key={`${s.label}-${i}`}>
            {i > 0 && (
              <div className="flex items-center gap-1.5 py-1 pl-[116px] text-[11.5px] text-muted-foreground max-sm:pl-0">
                <ArrowDown className="size-3 flex-none text-muted-foreground/70" />
                <span>{s.lossLabel || 'Lost'}:</span>
                <b className="font-semibold text-foreground/80 tabular-nums">
                  −{lost.toLocaleString()}
                </b>
                <span className="tabular-nums">
                  ({prev!.value > 0 ? ((lost / prev!.value) * 100).toFixed(1) : '0'}%)
                </span>
              </div>
            )}
            <div className="grid grid-cols-[104px_1fr_auto] items-center gap-3 py-[5px] max-sm:grid-cols-[1fr_auto]">
              <div className="overflow-hidden text-ellipsis whitespace-nowrap text-[12.5px] text-muted-foreground max-sm:col-span-2">
                {s.label}
              </div>
              <div className="h-3 overflow-hidden rounded-full bg-muted">
                <i
                  className="report-funnel-in block h-full min-w-3 rounded-full"
                  style={{
                    width: `${Math.max(0.8, (s.value / max) * 100)}%`,
                    background: color,
                    opacity: (1 - i * 0.18).toFixed(2),
                  }}
                />
              </div>
              <div className="min-w-[70px] text-right text-[15px] font-semibold tabular-nums text-foreground">
                {s.value.toLocaleString()}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
